<template>
  <div
    class="insights-focus-time-widget insights-widget"
  >
    <div class="widget-wrapper">
      <div class="widget-common widget-small widget-full-height">
        <div class="widget-title d-flex align-items-baseline">
          {{ title }}
          <i
            v-if="infoTooltip"
            v-tooltip="infoTooltipOptions"
            class="hi hi-info-filled ml-5"
            :title="infoTooltip"
          />

          <i
            v-if="isDataCropped"
            v-tooltip="tooltipOptions"
            class="hi hi-warning data-cropped-warning"
            :title="dataCroppedTooltip"
          />
        </div>
        <div
          class="widget-body"
          :class="bodyClasses"
        >
          <template v-if="isReady">
            <InsightsNoData
              v-if="showNoDataState"
              :type="emptyReason"
            />
            <div
              v-else
              class="widget-details"
            >
              <div class="border-right pr-5">
                <div class="d-flex flex-column mb-15">
                  <span class="value">
                    {{ value }}
                  </span>
                  <span class="property"> Focus time </span>
                </div>
                <div class="d-flex flex-column mb-15">
                  <span class="value">
                    {{ sessions }}
                  </span>
                  <span class="property"> Focus sessions </span>
                </div>
                <div class="d-flex flex-column">
                  <span class="value">
                    {{ sessionAverage }}
                  </span>
                  <span class="property">Avg. session length</span>
                </div>
              </div>
              <div
                v-if="isReady"
                :class="{ empty: !hasChartData }"
                class="chart-wrapper d-flex flex-column align-items-center justify-content-center"
              >
                <AppChartDonut
                  :values="chartData"
                  :text="text"
                  :size="chartSize"
                  :inner-radius="chartRadius"
                  :sort="false"
                  with-tooltips
                />
              </div>
            </div>
          </template>
        </div>
        <div
          v-if="showWidgetBenchmarks"
          class="widget-footer-bordered"
        >
          <InsightsBenchmarksAverages
            :averages="benchmarksAverages"
            :pins="benchmarkPins"
            :is-loading="!isReady"
            :max="100"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { INSIGHTS } from '~/app_constants';
import { formatDuration } from '~/utils/time_utils';

import { Insights, InsightsTracking } from '~/mixins';
import { mapGetters } from 'vuex';
import AppChartDonut from '~/components/AppChartDonut.vue';

// There are 5 possible states for this widget's UI.
//
// 1. focus_time: { data_cropped: false } means no activity was tracked. We show InsightsNoData.vue inside the widget wrapper.
// 2. focus_time: { data_cropped: true } means we don't know what's going on with the data, so we show the warning icon and `-` values.
// 3. focus_time: { data_cropped: false, [widget keys] } means everything is fine and synchronized.
// 4. focus_time: { data_cropped: true, [widget keys] } means we only have "some" data (for now). We show the warning icon.

export default {
  name: 'InsightsFocusTimeWidget',
  components: {
    AppChartDonut,
    InsightsNoData: () => import('~/components/insights/InsightsNoData.vue'),
    InsightsBenchmarksAverages: () => import('~/components/insights/InsightsBenchmarksAverages.vue'),
  },
  mixins: [Insights, InsightsTracking],
  props: {
    title: {
      type: String,
      default: 'Focus Time',
    },
    chartSize: {
      type: Number,
      default: 86,
    },
    chartRadius: {
      type: Number,
      default: 36,
    },
  },
  data: () => ({
    tooltipOptions: Object.freeze({ html: true, sanitize: false }),
    widgetKey: INSIGHTS.api.focusTime,
    dataCroppedTooltip:
      'Daily focus data collection started recently, so these metrics do not fully represent the selected time period.',
  }),
  computed: {
    ...mapGetters({
      widgetData: 'focusTime',
    }),
    ...mapGetters(['showBenchmarksAverages', 'showWidgetBenchmarks', 'remoteVsOffice']),
    showNoDataState() {
      return this.isEmptyWidget && !this.isDataCropped;
    },
    activeBenchmarkKeys() {
      if (!this.remoteVsOffice) {
        return [{ key: 'standard', percentageKey: 'percentage' }];
      }

      const keys = [];

      if (this.widgetData?.percentage_remote != null) {
        keys.push({ key: 'remote', percentageKey: 'percentage_remote' });
      }
      if (this.widgetData?.percentage_on_site != null) {
        keys.push({ key: 'on_site', percentageKey: 'percentage_on_site' });
      }

      return keys;
    },
    benchmarksAverages() {
      const benchmarks = this.widgetData?.benchmarks ?? {};

      if (!this.remoteVsOffice) {
        return benchmarks;
      }

      return this.activeBenchmarkKeys.reduce((result, { key }) => {
        if (benchmarks[key]) {
          result[key] = benchmarks[key];
        }

        return result;
      }, {});
    },
    hasChartData() {
      return !!this.widgetData?.percentage;
    },
    benchmarkPins() {
      return this.activeBenchmarkKeys.map(({ key, percentageKey }) => ({
        key,
        value: Math.round(this.widgetData?.[percentageKey] * 100),
      }));
    },
    totalTime() {
      // We are not receiving total time from the widget request
      // So this calculates the remaining percentage's time (time not in focus) to show on the tooltip
      return this.hasChartData
        ? ((1 - this.widgetData?.percentage) * this.widgetData?.total_time) / this.widgetData?.percentage
        : null;
    },
    chartData() {
      const value = this.widgetData?.percentage * 100 || 0;
      const timeInFocus = this.widgetData?.total_time;

      return [
        { key: 'focus-time', value, tooltip: this.formatTooltip(timeInFocus) },
        { key: 'empty', value: 100 - value, tooltip: this.formatTooltip(this.totalTime) },
      ];
    },
    text() {
      if (this.isReady) {
        return this.widgetData && 'percentage' in this.widgetData
          ? `${Math.round(this.widgetData.percentage * 100)}%`
          : '-';
      }

      return null;
    },
    value() {
      return this.widgetData && 'total_time' in this.widgetData
        ? formatDuration(this.widgetData.total_time, { showSeconds: 'never' })
        : '-';
    },
    sessions() {
      return this.widgetData && 'sessions_count' in this.widgetData
        ? Math.round(this.widgetData.sessions_count * 10) / 10
        : '-';
    },
    sessionAverage() {
      return this.widgetData?.per_session ? formatDuration(this.widgetData.per_session, { showSeconds: 'never' }) : '-';
    },
    isDataCropped() {
      return this.widgetData?.data_cropped;
    },
    infoTooltipOptions() {
      const template = INSIGHTS.tooltips.focusTime.template;

      return {
        ...this.tooltipOptions,
        template,
      };
    },
    infoTooltip() {
      return INSIGHTS.tooltips.focusTime.content;
    },
  },

  methods: {
    formatTooltip(value) {
      return value ? formatDuration(value, { showSeconds: 'never', style: 'units' }) : '';
    },
  },
};
</script>
